import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { EmptyState } from "src/components/EmptyState";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { useGetOfferConfigurationsByFormTemplateIdKey } from "src/generated/hooks/form-service/queries";
import {
  deleteFormRule,
  deleteOfferConfiguration,
  getOfferConfigurationsByFormTemplateId,
  GetOfferConfigurationsByFormTemplateIdResponse,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { UseOrganizationType } from "src/hooks/useOrganization";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import EmptyBox from "src/images/empty-box.svg?react";
import { RuleType } from "src/types/formEngineRule";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { useFormTemplateContext } from "../formTemplateContext";
import { AddRuleModal } from "./AddRuleModal";
import { ClearLowerRankedDrawer } from "./ClearLowerRankedDrawer";
import { FormEngineRuleCard } from "./FormEngineRuleCard";
import { FormEngineRuleDrawer } from "./FormEngineRuleDrawer";
import { RuleImportError } from "./importExport/helpers";
import { ImportExportRulesMenu } from "./importExport/ImportExportRulesMenu";
import { ImportRulesFromJSON } from "./importExport/ImportRulesFromJSON";
import { ImportRulesFromOtherDialog } from "./importExport/ImportRulesFromOtherDialog";
import { RuleImportErrorsModal } from "./importExport/RuleImportErrorsModal";
import { LegacyRuleCard } from "./LegacyRuleCard";
import { LegacyRuleDrawer } from "./LegacyRuleDrawer";
import * as LegacyRules from "./legacyRules";
import { DELETE_LEGACY_FORM_TEMPLATE_RULE } from "./mutations";
import { OfferConfigurationCard } from "./OfferConfigurationCard";
import { OfferConfigurationDrawer } from "./OfferConfigurationDrawer";
import {
  ClearLowerRankedConfiguration,
  OfferExpirationConfiguration,
} from "./offerConfigurationTypes";
import { GET_LEGACY_FORM_TEMPLATE_RULE_BY_FORM_TEMPLATE_RULE_ID } from "./queries";
import * as RuleUtils from "./utils";

export type Props = {
  organization: UseOrganizationType;
};

export const RulesTab: React.FC<Props> = ({ organization }) => {
  /* permissions */
  const canEditFormTemplateRules = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  /* contexts */
  const toast = useAvelaToast();

  const { ruleId, editRuleIsOpen } = useQueryParams();

  const { formTemplate } = useFormTemplateContext();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Delete rule?",
    body: "Deleting this rule will stop the automatic behavior immediately. This cannot be undone or recovered.",
    cancelButton: { label: "Cancel" },
    confirmButton: { label: "Yes, delete" },
  });

  /* state */

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [selectedRuleType, setSelectedRuleType] = useState<RuleType | "">("");

  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);

  const [isEditingLegacyRule, setIsEditingLegacyRule] = useState(false);
  const [newLegacyRule, setNewLegacyRule] =
    useState<LegacyRules.FormikType | null>(null);

  const [isFormEngineRulePanelOpen, setIsFormEngineRulePanelOpen] = useState(
    editRuleIsOpen ? editRuleIsOpen?.toString() === "true" : false
  );

  const [formEngineRuleToEdit, setFormEngineRuleToEdit] = useState<{
    id: string;
  } | null>(ruleId ? { id: ruleId as string } : null);

  const [initialRuleType, setInitialRuleType] = useState<RuleType | null>(null);

  const [isOfferConfigurationDrawerOpen, setIsOfferConfigurationDrawerOpen] =
    useState(false);

  const [offerConfigurationToEdit, setOfferConfigurationToEdit] =
    useState<OfferExpirationConfiguration | null>(null);

  const [isClearLowerRankedDrawerOpen, setIsClearLowerRankedDrawerOpen] =
    useState(false);

  const [clearLowerRankedToEdit, setClearLowerRankedToEdit] =
    useState<ClearLowerRankedConfiguration | null>(null);

  /* import / export */
  const [showImportJSON, setShowImportJSON] = useState(false);
  const [ruleErrors, setRuleErrors] = useState<RuleImportError[]>([]);
  const [errorContext, setErrorContext] = useState<"import" | "export">(
    "import"
  );
  const importFromOtherDialog = useDisclosure();
  const errorsModal = useDisclosure();

  /*
  queries and mutations
  */
  const { remoteData: rulesRemoteData, refetch: refetchRules } =
    useRemoteDataQuery<
      GQL.GetLegacyFormTemplateRuleByFormTemplateId,
      GQL.GetLegacyFormTemplateRuleByFormTemplateIdVariables
    >(GET_LEGACY_FORM_TEMPLATE_RULE_BY_FORM_TEMPLATE_RULE_ID, {
      variables: {
        formTemplateId: formTemplate.id,
        enrollmentPeriodId: formTemplate.enrollment_period.id,
      },
      fetchPolicy: "no-cache",
    });

  const offerConfigQuery = useQuery({
    queryKey: [
      useGetOfferConfigurationsByFormTemplateIdKey,
      { path: { formTemplateId: formTemplate.id } },
    ],
    queryFn: () =>
      getOfferConfigurationsByFormTemplateId({
        path: { formTemplateId: formTemplate.id },
      }).then((r) => r.data),
  });

  const offerConfigRD: RD.RemoteData<
    Error,
    GetOfferConfigurationsByFormTemplateIdResponse
  > = useMemo(
    () =>
      RD.fromRestResult(offerConfigQuery).mapError(
        (error) => error ?? new Error("Failed to load offer configurations")
      ),
    [offerConfigQuery]
  );

  const [deleteLegacyRules] = useRemoteDataMutation<
    GQL.DeleteLegacyFormTemplateRule,
    GQL.DeleteLegacyFormTemplateRuleVariables
  >(DELETE_LEGACY_FORM_TEMPLATE_RULE);

  /* form engine rule model */

  const deleteFormEngineRuleMutation = useMutation({
    mutationFn: (id: string) =>
      deleteFormRule({
        path: { formTemplateId: formTemplate.id, id },
      }),
  });

  const mappedData = useMemo(() => {
    return RD.toTuple(rulesRemoteData, offerConfigRD).map(
      ([data, offerConfigResponse]) => {
        const legacyRule = LegacyRules.fromGQL(data);
        const schools = data.school;
        const hasPrograms = data.school.some((x) =>
          x.grades.some((y) => y.program)
        );
        const formTemplates = data.form_template;
        const formEngineRules =
          data.form_template_by_pk?.form_engine_rules || [];
        const offerConfigurationRows = offerConfigResponse.data;
        const tags = data.enrollment_period_tag || [];
        const sections = data.form_template_by_pk?.sections || [];

        // Check if the form template has school selection sections
        const hasSchoolSelectionSection = sections.some(
          (section) =>
            section.type ===
            GQL.form_template_section_type_enum.SchoolRankingSection
        );
        const isSchoolRankingEnabled = sections.some(
          (section) =>
            section.type ===
              GQL.form_template_section_type_enum.SchoolRankingSection &&
            section.schools_ranking_section?.ranking_enabled === true
        );

        const tagIdToNameMap = new Map(tags.map((tag) => [tag.id, tag.name]));

        return {
          legacyRule,
          schools,
          hasPrograms,
          formTemplates,
          formEngineRules,
          offerRules: offerConfigurationRows,
          tags,
          tagIdToNameMap,
          hasSchoolSelectionSection,
          isSchoolRankingEnabled,
        };
      }
    );
  }, [rulesRemoteData, offerConfigRD]);

  /*
  callbacks
  */
  const refetchAll = useCallback(async () => {
    await refetchRules();
    await offerConfigQuery.refetch();
  }, [refetchRules, offerConfigQuery]);

  const handleImportComplete = useCallback(
    async (errors: RuleImportError[]) => {
      setErrorContext("import");
      setRuleErrors(errors);
      setShowImportJSON(false);
      await refetchAll();
    },
    [refetchAll]
  );

  const handleRuleTypeSelection = () => {
    if (selectedRuleType === "connect_forms") {
      // Create a new connect forms rule with default values
      const newRule: LegacyRules.FormikType = {
        id: window.crypto.randomUUID(),
        trigger: {
          id: window.crypto.randomUUID(),
          type: "",
          status: "",
          schoolIdsOperator: GQL.list_operator_enum.any,
          schoolIds: [],
        },
        action: {
          type: "",
          nextFormTemplateId: "",
        },
        nextStep: {
          body: "",
          title: "",
          subtitle: "",
          buttonText: "",
        },
      };

      // Create a new initialValues with the new rule
      const newInitialValues = newRule;

      setNewLegacyRule(newInitialValues);
      setIsEditingLegacyRule(true);
      setIsAddRuleModalOpen(false);
      setSelectedRuleType("");
    } else if (
      selectedRuleType === "auto_tag" ||
      selectedRuleType === "auto_placement" ||
      selectedRuleType === "auto_waitlist"
    ) {
      setFormEngineRuleToEdit(null);
      setInitialRuleType(selectedRuleType);
      setIsFormEngineRulePanelOpen(true);
      setIsAddRuleModalOpen(false);
      setSelectedRuleType("");
    } else if (selectedRuleType === "auto_revoke") {
      // Open the offer configuration drawer for auto revoke
      setOfferConfigurationToEdit(null);
      setIsOfferConfigurationDrawerOpen(true);
      setIsAddRuleModalOpen(false);
      setSelectedRuleType("");
    } else if (selectedRuleType === "clear_lower_ranked") {
      setClearLowerRankedToEdit(null);
      setIsClearLowerRankedDrawerOpen(true);
      setIsAddRuleModalOpen(false);
      setSelectedRuleType("");
    }
  };

  if (!canEditFormTemplateRules || !organization.hasData()) {
    return null;
  }

  if (showImportJSON) {
    return (
      <ImportRulesFromJSON
        formTemplateId={formTemplate.id}
        onBack={() => setShowImportJSON(false)}
        onComplete={handleImportComplete}
      />
    );
  }

  return (
    <GQLRemoteDataView remoteData={mappedData}>
      {({
        legacyRule,
        schools,
        hasPrograms,
        formTemplates,
        formEngineRules,
        offerRules,
        tags,
        tagIdToNameMap,
        hasSchoolSelectionSection,
        isSchoolRankingEnabled,
      }) => {
        // Create a submit handler for the side panel form

        const handleAddRule = () => {
          setIsAddRuleModalOpen(true);
        };

        return (
          <Flex direction="column" gap="6" marginTop="2">
            {confirmationDialog}

            <Flex justifyContent="flex-end" gap={4}>
              <ImportExportRulesMenu
                formTemplateId={formTemplate.id}
                onImportFromJSON={() => setShowImportJSON(true)}
                onImportFromOther={importFromOtherDialog.onOpen}
                onExportErrors={(errors) => {
                  setErrorContext("export");
                  setRuleErrors(errors);
                }}
              />
            </Flex>

            <ImportRulesFromOtherDialog
              isOpen={importFromOtherDialog.isOpen}
              onClose={importFromOtherDialog.onClose}
              targetFormTemplateId={formTemplate.id}
              organization={organization}
              onComplete={handleImportComplete}
            />

            {ruleErrors.length > 0 && (
              <Alert status="error" rounded="md">
                <AlertIcon />
                <AlertDescription>
                  {errorContext === "export"
                    ? "Some rules could not be exported."
                    : "Some rules could not be imported."}
                </AlertDescription>
                <Button
                  variant="link"
                  colorScheme="gray"
                  ml={2}
                  onClick={errorsModal.onOpen}
                >
                  See details.
                </Button>
                <CloseButton ml="auto" onClick={() => setRuleErrors([])} />
              </Alert>
            )}

            <RuleImportErrorsModal
              context={errorContext}
              isOpen={errorsModal.isOpen}
              onClose={errorsModal.onClose}
              errors={ruleErrors}
            />

            <AddRuleModal
              hasSchoolSelectionSection={hasSchoolSelectionSection}
              isSchoolRankingEnabled={isSchoolRankingEnabled}
              lotteryOffersEnabled={formTemplate.lottery_offers_enabled}
              isAddRuleModalOpen={isAddRuleModalOpen}
              setIsAddRuleModalOpen={(state) => {
                setIsAddRuleModalOpen(state);
              }}
              selectedRuleType={selectedRuleType}
              setSelectedRuleType={(ruleType) => {
                setSelectedRuleType(ruleType);
              }}
              hasExistingLegacyRules={legacyRule !== null}
              hasExistingClearLowerRanked={offerRules.some(
                (rule) => rule.configuration_type === "CLEAR_LOWER_RANKED"
              )}
              hasPrograms={hasPrograms}
              handleRuleTypeSelection={() => {
                handleRuleTypeSelection();
              }}
            />

            {/* Search input */}
            {(legacyRule !== null ||
              formEngineRules.length > 0 ||
              offerRules.length > 0) && (
              <Box w="100%">
                <SearchInput
                  placeholder="Search rules..."
                  value={searchQuery}
                  onSearchChange={setSearchQuery}
                  onClear={() => setSearchQuery("")}
                  variant="leftIcon"
                />
              </Box>
            )}

            {/* Display both legacy and new rules */}
            {legacyRule !== null ||
            formEngineRules.length > 0 ||
            offerRules.length > 0 ? (
              <Flex direction="column" gap="4" key="rules-list">
                {/* Add rule button */}
                <Flex gap={2} mb={4}>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={handleAddRule}
                  >
                    Add rule
                  </Button>
                </Flex>

                {/* Legacy Connect Form Rule */}
                {legacyRule !== null &&
                  (!searchQuery.trim() ||
                    "connect forms"
                      .toLowerCase()
                      .includes(searchQuery.toLowerCase().trim())) && (
                    <LegacyRuleCard
                      legacyRules={legacyRule}
                      confirm={confirm}
                      toast={toast}
                      refetch={refetchAll}
                      deleteRule={async (variables) => {
                        await deleteLegacyRules({ variables });
                      }}
                      formTemplate={formTemplate}
                      formTemplates={formTemplates}
                      onEditClick={async () => {
                        setNewLegacyRule(legacyRule);
                        setIsEditingLegacyRule(true);
                      }}
                    />
                  )}

                {/* Form Engine Rule Cards */}
                {formEngineRules
                  .filter((rule) => rule.rule_type)
                  .map((r) => ({ data: r, info: new RuleUtils.RuleInfo(r) }))
                  .filter((rule) => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase().trim();
                    const name = rule.info.name.toLowerCase();
                    const description = rule.info.description.toLowerCase();
                    const tagNames = rule.info.tag_ids
                      .map((id) => tagIdToNameMap.get(id)?.toLowerCase() || "")
                      .join(" ");
                    return (
                      name.includes(query) ||
                      description.includes(query) ||
                      tagNames.includes(query)
                    );
                  })
                  .sort((lhs, rhs) =>
                    lhs.info.name.localeCompare(rhs.info.name, undefined, {
                      sensitivity: "base",
                    })
                  )
                  .map((rule, index: number) => (
                    <FormEngineRuleCard
                      key={`form-engine-rule-card-${index}`}
                      formTemplateId={formTemplate.id}
                      index={index}
                      tags={tags}
                      tagIdToNameMap={tagIdToNameMap}
                      rule={rule.data}
                      ruleInfo={rule.info}
                      onRefetch={refetchAll}
                      onClick={async () => {
                        if (await confirm()) {
                          try {
                            await deleteFormEngineRuleMutation.mutateAsync(
                              rule.data.id
                            );

                            toast({
                              title: "Success",
                              description: "Form engine rule deleted",
                              status: "success",
                            });

                            await refetchAll();
                          } catch (error) {
                            console.error(error);
                            toast.error({
                              title: "Failed to delete rule",
                              description:
                                "An error occurred while deleting the rule.",
                            });
                          }
                        }
                      }}
                      onEditClick={() => {
                        setInitialRuleType(null);
                        setFormEngineRuleToEdit({ id: rule.data.id });
                        setIsFormEngineRulePanelOpen(true);
                      }}
                    />
                  ))}
                {/* offer configuration rules */}
                {offerRules
                  .filter((offerRule) => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase().trim();
                    const name = (
                      offerRule.name || "offer configuration"
                    ).toLowerCase();
                    const description = (
                      offerRule.description || ""
                    ).toLowerCase();
                    return (
                      name.includes(query) ||
                      description.includes(query) ||
                      "auto offer".includes(query)
                    );
                  })
                  .map((offerRule, index) => (
                    <OfferConfigurationCard
                      key={`offer-configuration-${index}`}
                      index={index}
                      offerConfiguration={offerRule}
                      onRefetch={refetchAll}
                      onDelete={async () => {
                        if (await confirm()) {
                          try {
                            await deleteOfferConfiguration({
                              path: { id: offerRule.id },
                            });

                            toast({
                              title: "Success",
                              description: "Rule deleted",
                              status: "success",
                            });

                            await refetchAll();
                          } catch (error) {
                            console.error(error);
                            toast.error({
                              title: "Failed to delete rule",
                              description:
                                "An error occurred while deleting the rule.",
                            });
                          }
                        }
                      }}
                      onEditClick={() => {
                        if (
                          offerRule.configuration_type === "CLEAR_LOWER_RANKED"
                        ) {
                          setClearLowerRankedToEdit(
                            offerRule as ClearLowerRankedConfiguration
                          );
                          setIsClearLowerRankedDrawerOpen(true);
                        } else if (
                          offerRule.configuration_type === "OFFER_EXPIRATION"
                        ) {
                          setOfferConfigurationToEdit(
                            offerRule as OfferExpirationConfiguration
                          );
                          setIsOfferConfigurationDrawerOpen(true);
                        }
                      }}
                      schools={schools}
                    />
                  ))}

                {/* Add rule button */}
                <Flex gap={2} mt={4}>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={handleAddRule}
                  >
                    Add rule
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <EmptyState
                minHeight="calc(100vh - 38rem)"
                description="Add a rule to set workflows in motion"
                heading="No fancy rules yet"
                Svg={EmptyBox}
                alignSelf="center"
                flexGrow={1}
                justifyContent="center"
              >
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={handleAddRule}
                >
                  Add rule
                </Button>
              </EmptyState>
            )}

            <LegacyRuleDrawer
              toast={toast}
              legacyRule={legacyRule}
              formTemplate={formTemplate}
              formTemplates={formTemplates}
              schools={schools}
              newRule={newLegacyRule}
              isOpen={isEditingLegacyRule}
              deleteLegacyRules={async (variables) => {
                await deleteLegacyRules({
                  variables,
                });
              }}
              setIsEditingLegacyRule={(state) => {
                setIsEditingLegacyRule(state);
              }}
              refetchLegacyRules={refetchAll}
              onClose={() => {
                setIsEditingLegacyRule(false);
                setNewLegacyRule(null);
              }}
            />
            <FormEngineRuleDrawer
              hasPrograms={hasPrograms}
              organizationId={organization.data.id}
              formTemplate={formTemplate}
              rule={formEngineRuleToEdit}
              schools={schools}
              tags={tags}
              unavailableSchoolIds={RuleUtils.unavailableAutoPlacementSchoolIds(
                formEngineRules,
                schools,
                formEngineRuleToEdit?.id
              )}
              componentId="form-engine-rule-form"
              initialRuleType={initialRuleType}
              isOpen={isFormEngineRulePanelOpen}
              onClose={() => {
                setIsFormEngineRulePanelOpen(false);
                setFormEngineRuleToEdit(null);
                setInitialRuleType(null);
              }}
              setIsFormEngineRulePanelOpen={(state) => {
                setIsFormEngineRulePanelOpen(state);
              }}
              setFormEngineRuleToEdit={(rule) => {
                setFormEngineRuleToEdit(rule);
              }}
              refetch={refetchAll}
            />
            <OfferConfigurationDrawer
              configuration={offerConfigurationToEdit}
              formTemplateId={formTemplate.id}
              schools={schools}
              isOpen={isOfferConfigurationDrawerOpen}
              onClose={() => {
                setIsOfferConfigurationDrawerOpen(false);
                setOfferConfigurationToEdit(null);
              }}
              refetch={refetchAll}
            />
            <ClearLowerRankedDrawer
              configuration={clearLowerRankedToEdit}
              formTemplateId={formTemplate.id}
              isOpen={isClearLowerRankedDrawerOpen}
              onClose={() => {
                setIsClearLowerRankedDrawerOpen(false);
                setClearLowerRankedToEdit(null);
              }}
              refetch={refetchAll}
            />
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};
